import React, { useEffect, useState } from 'react'
import { useTheme, Typography, Stack, Box, Tooltip } from '@mui/material';
import { AreaChart, Area, ResponsiveContainer, XAxis } from 'recharts';
import { Tooltip as RechartsTooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import { colord } from "colord";

import { QueryInfo } from 'system/query/QueryInfo';
import { DashboardQueryDrilldownDialog } from 'components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog';
import { Query } from 'system/query/Query';
import ISONumber from 'components/formatting/ISONumber';
import { MakeDrilldownQueryFromHistogramIndex } from 'components/widgets/utility/DrilldownQuery';
import { defaultSettings, SparklineSettingsStateType } from './SparklineSettings';
import SparklineTooltip from './SparklineTooltip';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import { calculatePercentChange, getPercentChangeTextColor, toPercentChangeString } from '../PercentChange/PercentChange';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import { Widget, WidgetRequest, WidgetResponseItem } from 'system/widget/Widget';

interface SparklineContentProps {
  apiResponseBody?: WidgetResponseItem;
  apiRequestInfo?: WidgetRequest;
  queryDisplayTitle?: string;
  widget?: Widget;
  queryInfo?: QueryInfo;
  activeQueryIds?: string[];
  isDrilldown?: boolean;
  defaultState?: SparklineSettingsStateType;
  value: number;
  widgetQuery?: Query;
}

export default function SparklineContent(props: SparklineContentProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [drilldownTitle, setDrilldownTitle] = useState<string | null>(null);
  const [drilldownQuery, setDrilldownQuery] = useState<Query | null>(null);
  const [histogram, setHistogram] = useState<any | null>(null);
  const [hovered, setHovered] = useState<boolean>(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  let isDrilldown = props.isDrilldown || false;

  useEffect(() => {
    if (isDrilldown) {
      setHistogram(props.apiResponseBody);
    } else {
      setHistogram(props.apiResponseBody?.histogram);
    }
  }, [props.apiResponseBody]);

  const labels = isDrilldown ? histogram?.timeline_timestamps : histogram?.labels;

  const field = props.apiRequestInfo?.requestInit?.body?.aggs?.histogram?.field;

  const fieldLabel = isDrilldown ? field : histogram?.series?.[0]?.field_label;
  let yAxisLabel = i18n.t('records');
  let index = 0;
  if (props.apiRequestInfo?.requestInit?.body?.aggs?.histogram?.aggs) {
    index = 1;
    yAxisLabel = histogram?.series?.[index]?.field_label;
  }
  const subLabel = histogram?.series?.[index]?.label || null;

  const data = (isDrilldown ? histogram?.timeline?.map((row: any, index: number) => {
    return {
      date: histogram?.timeline_timestamps[index],
      value: row ?? 0
    };
  }) : histogram?.series?.[index]?.values.map((row: any, index: number) => {
    // If there is a decimal, round to 2 places for cleaner display
    // This could be an option in the future
    if (row % 1 !== 0) {
      row = row.toFixed(2);
    }
    return {
      date: labels[index],
      value: parseFloat(row ?? 0) || 0,
      label: yAxisLabel,
      subLabel: subLabel
    };
  })) || [];

  const percentChange = calculatePercentChange(data?.map((row: any) => row.value));
  const percentChangeString = toPercentChangeString(i18n, percentChange);
  const percentChangeColor = getPercentChangeTextColor(theme, percentChange);

  const currentState = props.defaultState || { ...defaultSettings, ...props.widget?.data?.settings };
  const dotRadius = 3;

  const hasData = data && data.length > 0;
  const dotMarginSize = dotRadius + currentState.dotSize/2;
  const areaFillColor = currentState.lineColor ?? theme.colors.primary.main;

  return (
    <WidgetLayout
      header={
        <>
          {!isDrilldown && (
            <WidgetTitle
              alignment={props.widget?.data?.settings?.widgetTitleAlignment}
              customTitle={props.widget?.data?.settings?.customWidgetTitle}
              defaultTitle={yAxisLabel}
              settingsTitle={props.widget?.data?.settings?.widgetTitle}
              widgetQueryTitle={props.queryDisplayTitle} />
          )}
          {hasData &&
            <>
              {(currentState.showPercentChange || currentState.showTotal) &&
                <Stack
                  direction="row"
                  sx={{
                    paddingLeft: 2,
                    alignItems: "center"
                  }}>
                  {currentState.showTotal &&
                    <Typography variant="h1" >
                      <ISONumber number={props.value} sx={{ fontSize: "2rem", fontWeight: "bold", paddingLeft: 1, textAlign: "left" }} />
                    </Typography>
                  }
                  {currentState.showPercentChange &&
                    <Typography variant="body1" sx={{ color: percentChangeColor, fontSize: "1.2rem", textAlign: "left", paddingLeft: 1 }}>
                      {percentChangeString}
                    </Typography>
                  }
                </Stack>
              }
            </>
          }
        </>
      }
    >
      {!hasData &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
      {hasData &&
        <>
          <Tooltip
            title={(hovered) ? <SparklineTooltip data={data} index={hoveredIndex} /> : ""}
            open={!!(hoveredIndex && hoveredIndex >= 0)}
            followCursor={true}>
            <Box sx={{
              height: "100%"
            }}>
              <ResponsiveContainer>
                <AreaChart style={{ cursor: "pointer" }}
                  data={data}
                  margin={{ top: dotMarginSize, right: 0, bottom: currentState.showDotMargin ? dotMarginSize : 0, left: 0 }}
                  onClick={(event: any) => {
                    if (isDrilldown) return;
                    const drilldownInfo = MakeDrilldownQueryFromHistogramIndex(histogram, field, event.activeTooltipIndex);
                    if (!drilldownInfo) return;
                    setDrilldownTitle(`${fieldLabel}: ${drilldownInfo?.title}`);
                    setDrilldownQuery(drilldownInfo?.query);
                  }}
                  onMouseMove={(event: any) => {
                    setHovered(true);
                    setHoveredIndex(event.activeTooltipIndex);
                  }}
                  onMouseLeave={() => {
                    setHovered(false);
                    setHoveredIndex(null);
                  }}
                >
                  <Area
                    type="monotone"
                    dot={currentState.showDots && {
                      stroke: currentState.lineColor ?? theme.palette.primary.main,
                      strokeWidth: currentState.dotSize ?? 2,
                      fill: areaFillColor,
                      r: dotRadius,
                      opacity: 1
                    }}
                    dataKey="value"
                    stroke={currentState.lineColor ?? theme.palette.primary.main}
                    strokeWidth="2"
                    fill={colord(areaFillColor).alpha(0.1).toRgbString()}
                  />
                  {currentState.showXAxis &&
                    <XAxis dataKey="date" />
                  }
                  <RechartsTooltip active={false} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Tooltip>
          {drilldownQuery && drilldownTitle && QueryInfo && !isDrilldown &&
            <DashboardQueryDrilldownDialog
              title={drilldownTitle}
              queryInfo={props.queryInfo}
              onClose={() => setDrilldownQuery(null)}
              activeQueryIds={props.activeQueryIds}
              drilldownQuery={drilldownQuery}
              widgetRequestInfo={props.widget?.request_info}
            />
          }
        </>
      }
    </WidgetLayout>
  );
}
